import { Typography, TextField, FormControl, MenuItem, Grid } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { NumericFormat } from 'react-number-format';
import { regions } from '../../helper/regions';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import { useUpdateClientMutation } from '../../services/features/clientApi/clientApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

/**
 * Component for managing address fields.
 *
 * @param {Object} props - The component's props.
 * @param {Object} props.options - Formik form options.
 *
 * @returns {JSX.Element} The JSX element for address fields.
 */
export const AddressFields = (props: any) => {
	const [updateClient] = useUpdateClientMutation();

	const quoteId = useSelector((state: RootState) => state.quoteSlice.quoteId);

	const formik = props.options;

	const { quoteGroup, loadingProducts, setLoadingProducts, isReferred, isDeclined } =
		useQuoteContext();

	const readonly = isDeclined; //isReferred || isDeclined;

	// Create menu items for regions
	const regionMenuItems = Object.entries(regions).map(([regionName]) => (
		<MenuItem key={regionName} value={regionName}>
			{regionName}
		</MenuItem>
	));

	// Handle region change in the Select component
	const handleRegionChange = (event: SelectChangeEvent) => {
		formik.setFieldValue('region', event.target.value);
		updateAddress(event.target.value);
	};

	// Update address data and send it to the server
	const updateAddress = async (newRegion?: string) => {
		if (quoteGroup && !loadingProducts) {
			setLoadingProducts(true);
			const region = newRegion ?? formik.values.region;
			const newQuoteClient = {
				id: quoteGroup.quoteClient.id,
				// ...quoteGroup.quoteClient,
				stateCode: newRegion ?? quoteGroup.quoteClient.stateCode,
				postcode:
					formik.values.postcode.length === 4
						? formik.values.postcode
						: quoteGroup.quoteClient.postcode,
				address1:
					formik.values.streetName.length > 0
						? formik.values.streetName
						: quoteGroup.quoteClient.address1,
				address2:
					formik.values.city.length > 0
						? formik.values.city
						: quoteGroup.quoteClient.address2,
				// ...(newRegion && { stateCode: region }),
				// ...(formik.values.postcode.length === 4 && {
				// 	postcode: formik.values.postcode,
				// }),
				// ...(formik.values.streetName.length > 0 && {
				// 	address1: formik.values.streetName,
				// }),
				// ...(formik.values.city.length > 0 && { address2: formik.values.city }),
			};
			await updateClient({ quoteId: quoteId ?? 0, clientData: newQuoteClient })
				.unwrap()
				.catch((error) => console.log('Error in updating address', error));
			setLoadingProducts(false);
		}
	};

	return (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<FormControl fullWidth>
					<Typography variant='subtitle1'>Street address</Typography>
					<TextField
						placeholder='Enter address here'
						name='streetName'
						onChange={(e: any) => formik.setFieldValue('streetName', e.target.value)}
						fullWidth
						disabled={readonly || loadingProducts}
						value={formik.values.streetName}
						error={formik.touched.streetName && Boolean(formik.errors.streetName)}
						helperText={formik.touched.streetName && formik.errors.streetName}
						onBlur={() => updateAddress()}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={4}>
				<FormControl fullWidth>
					<Typography variant='subtitle1'>City</Typography>
					<TextField
						placeholder='Enter city'
						name='city'
						onChange={(e: any) => formik.setFieldValue('city', e.target.value)}
						fullWidth
						disabled={readonly || loadingProducts}
						value={formik.values.city}
						error={formik.touched.city && Boolean(formik.errors.city)}
						helperText={formik.touched.city && formik.errors.city}
						onBlur={() => updateAddress()}
					/>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={4}>
				<FormControl fullWidth>
					<Typography variant='subtitle1'>State</Typography>
					<Select
						value={formik.values.region}
						disabled={readonly || loadingProducts}
						onChange={handleRegionChange}
						fullWidth
						name='region'
						label='Select state'
					>
						{regionMenuItems}
					</Select>
				</FormControl>
			</Grid>
			<Grid item xs={12} sm={4}>
				<FormControl fullWidth>
					<Typography variant='subtitle1'>Postcode</Typography>
					<NumericFormat
						value={formik.values.postcode}
						customInput={TextField}
						decimalScale={0}
						fixedDecimalScale
						allowNegative={false}
						onValueChange={({ floatValue }) =>
							formik.setFieldValue('postcode', `${floatValue}`)
						}
						fullWidth
						placeholder='Enter postcode'
						name='postcode'
						disabled={readonly || loadingProducts}
						error={formik.touched.postcode && Boolean(formik.errors.postcode)}
						helperText={formik.touched.postcode && formik.errors.postcode}
						onBlur={() => {
							if (formik.values.postcode.length === 4) updateAddress();
						}}
						aria-describedby='component-error-text'
						inputProps={{ maxLength: 4, minLength: 4 }}
					/>
				</FormControl>
			</Grid>
		</Grid>
	);
};
